import { useCallback, useMemo, useState } from 'react';
import { useDataContext } from '../../../context/data-context/use-data-context';
import { GetAddresses } from '../../../global-types';
import { EnvelopeType } from '../../../global-types/envelope';
import { useFeatureFlags } from '../../../hooks';
import useAddressSelection from '../../../hooks/use-address-selection';
import { useIsOneToMany } from '../../../hooks/use-is-one-to-many';
import { useSelectedEnvelope } from '../../../hooks/use-selected-envelope';
import { useSystemErrorHandling } from '../../../hooks/use-system-error-handling';
import { getAddresses as fetchAddresses } from '../../../services/address';
import { mapAddressesByType } from '../../../utils/address/map-address-by-type';
import { mapAddressesToContacts } from '../../../utils/address/map-addresses-to-contacts';

export const useGetAddresses = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { contacts, handleSetSender, handleSetRecipientContacts } = useAddressSelection();
  const isOneToMany = useIsOneToMany();
  const { IS_MULT_ADDRESS_ENABLED } = useFeatureFlags();

  const { selectedEnvelope } = useSelectedEnvelope();

  const isNewOneToManyFlow = useMemo(
    () => isOneToMany && IS_MULT_ADDRESS_ENABLED,
    [IS_MULT_ADDRESS_ENABLED, isOneToMany],
  );

  const {
    initializedDataState: { data: initializedData },
    setAddressData,
  } = useDataContext();
  const { onSystemError } = useSystemErrorHandling();

  const projectId = initializedData?.project_id;
  const accountId = initializedData?.account_id;

  const getAddresses: (enableRecipientAddresses: boolean) => void = useCallback(
    (enableRecipientAddresses = false) => {
      // const envelope = getEnvelopeSelection();
      const isBlankEnvelope = selectedEnvelope === EnvelopeType.Blank;

      if (isBlankEnvelope) return;

      if (projectId) {
        setIsLoading(true);
        fetchAddresses(projectId)
          .then((response: GetAddresses) => {
            if (!response.data) {
              handleSetRecipientContacts([]);
              return;
            }

            if (isNewOneToManyFlow) {
              // get the recipient addresses and the sender address
              const { senderContact, recipientContacts } = mapAddressesToContacts({
                addresses: response.data,
                accountId,
              });

              // assign the sender address to the context
              senderContact &&
                !isBlankEnvelope &&
                handleSetSender({ sender: senderContact, enableRecipientAddresses: enableRecipientAddresses });
              // assign the recipient addresses to the context
              selectedEnvelope === EnvelopeType.ReturnRecipient && handleSetRecipientContacts(recipientContacts);
            } else {
              // by default, for 1:1 card, we want to set the sender and recipient address to the data context
              const { recipient, sender } = mapAddressesByType(response.data);
              setAddressData({ recipient, sender });
            }
          })
          .catch((error) => {
            onSystemError(error);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    },
    [
      accountId,
      handleSetRecipientContacts,
      handleSetSender,
      isNewOneToManyFlow,
      onSystemError,
      projectId,
      selectedEnvelope,
      setAddressData,
    ],
  );

  return { getAddresses, isOpen, setIsOpen, contacts, isLoading };
};
